// store/store.js
import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { Instagram, MessageCircle, MessageSquare, Phone } from "lucide-react";
import { SocialIcon } from "react-social-icons";
import { socialPlatformsApi } from "../services/socialPlatformsApi";
import { createAgentsSlice } from "./slices/agents/createAgentsSlice";
import { createAgentUiSlice } from "./slices/agents/agentUiSlice";
import { createAgentTemplatesSlice } from "./slices/agents/agentTemplatesSlice";
import { createPlaygroundSlice } from "./slices/agents/agentPlaygroundSlice";
import { createTranslationSlice } from "./slices/translationSlice";
import { createCalendarSlice } from "./slices/calendar/CalendarSlice";

const useStore = create(
  devtools((set, get) => ({
    // All slices
    ...createCalendarSlice(set, get),
    ...createAgentsSlice(set, get),
    ...createAgentUiSlice(set, get),
    ...createAgentTemplatesSlice(set, get),
    ...createPlaygroundSlice(set, get),
    ...createTranslationSlice(set, get),

    platforms: [
      {
        id: "instagram",
        name: "Instagram",
        icon: <Instagram className="h-5 w-5" />,
        color:
          "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600",
        textColor: "text-[#E4405F]",
      },
      {
        id: "whatsapp",
        name: "WhatsApp",
        icon: (
          <SocialIcon
            network="whatsapp"
            style={{ height: 24, width: 24 }}
            bgColor="#25D366"
          />
        ),
        color: "bg-[#25D366] hover:bg-[#1EBE5B]",
        textColor: "text-[#25D366]",
      },
      {
        id: "messenger",
        name: "Messenger",
        icon: (
          <SocialIcon
            network="facebook"
            style={{ height: 24, width: 24 }}
            bgColor="#0084FF"
          />
        ),
        color: "bg-[#0084FF] hover:bg-[#006FCC]",
        textColor: "text-[#0084FF]",
      },
      {
        id: "viber",
        name: "Viber",
        icon: <Phone className="h-5 w-5" />,
        color: "bg-[#665CAC] hover:bg-[#5A4E9C]",
        textColor: "text-[#665CAC]",
      },
    ],
    currentPlatform: null,

    // Configurations slice
    configurations: {
      instagram: [],
      whatsapp: [],
      messenger: [],
      viber: [],
    },
    // Form slice
    formData: {
      name: "",
      avatarUrl: "",
      AgentId: "",
      apiGatewayUrl: "",
      welcomeMessage: "",
      viberToken: "",
      viberAccountId: "",
      instagramToken: "",
      instagramBusinessId: "",
      instagramAccountId: "",
      instagramPageId: "",
      instagramPageName: "",
      instagramAppId: "",
      instagramAppSecret: "",
      isInstagramExternalLogin: false,
      facebookToken: "",
      facebookAccountId: "",
      facebookAppId: "",
      facebookAppSecret: "",
      isFacebookExternalLogin: false,
      whatsappToken: "",
      whatsappBusinessId: "",
      whatsappBusinessPhoneNumber: "",
      phoneNumberId: "",
      // Selected template name to use for re-opening conversations
      TemplateName: "",
      whatsAppAppId: "",
      whatsAppAppSecret: "",
      isWhatsAppExternalLogin: false,
      messageDelivered: false,
      messageSeen: false,
      conversationStarted: false,
      active: false,
      development: false,
      messageReceived: false,
      messageMedia: false,
      messageReaction: false,
      messagePostback: false,
    },

    // UI slice
    selectedConfig: null,
    isCreating: false,
    showDeleteModal: false,
    configToDelete: null,
    newConfigPermission: false,
    isLoadingConfigs: false,
    isConfigError: false,

    // Actions for platforms slice
    setCurrentPlatform: (platformData) => {
      // If it's already a full platform object, use it directly
      if (platformData && typeof platformData === "object" && platformData.id) {
        set({ currentPlatform: platformData });
        return;
      }

      // If it's a string (platform name or path), find the platform
      let platformName = platformData;
      if (platformData?.includes("/")) {
        platformName = platformData.split("/").pop();
      }

      // Find platform from the platforms array
      const platforms = get().platforms;
      const platformConfig = platforms.find((p) => p.id === platformName);

      if (platformConfig) {
        set({ currentPlatform: platformConfig });
      }
    },

    // Actions for configurations slice
    setConfigurations: (platformId, configs) => {
      if (!platformId) return;

      set((state) => ({
        configurations: {
          ...state.configurations,
          [platformId]: configs,
        },
      }));
    },

    updateConfiguration: (platformId, configId, configData) => {
      if (!platformId) return;

      set((state) => {
        const platformConfigs = [...state.configurations[platformId]];
        const index = platformConfigs.findIndex((c) => c.id === configId);

        if (index !== -1) {
          platformConfigs[index] = {
            ...platformConfigs[index],
            ...configData,
          };
        }

        return {
          configurations: {
            ...state.configurations,
            [platformId]: platformConfigs,
          },
        };
      });
    },

    // Actions for UI slice
    selectConfig: (configId) =>
      set((state) => {
        const tokensByConfig =
          state.platformIntegrations?.whatsapp?.tokensByConfig || {};
        const creds = configId
          ? tokensByConfig[configId] || { accessToken: "", userID: "" }
          : { accessToken: "", userID: "" };
        return {
          selectedConfig: configId,
          isCreating: false,
          platformIntegrations: {
            ...state.platformIntegrations,
            whatsapp: {
              ...state.platformIntegrations.whatsapp,
              accessToken: creds.accessToken || "",
              userID: creds.userID || "",
            },
          },
          formData: {
            ...state.formData,
            whatsappToken: creds.accessToken || "",
          },
        };
      }),

    showDeleteConfirmation: (configId) =>
      set({
        showDeleteModal: true,
        configToDelete: configId,
      }),

    hideDeleteConfirmation: () =>
      set({
        showDeleteModal: false,
        configToDelete: null,
      }),

    // Actions for form slice
    updateFormField: (fieldId, value) =>
      set((state) => ({
        formData: {
          ...state.formData,
          [fieldId]: value,
        },
      })),
    resetForm: () =>
      set({
        formData: {
          name: "",
          avatarUrl: "",
          AgentId: "",
          apiGatewayUrl: "",
          welcomeMessage: "",
          viberToken: "",
          viberAccountId: "",
          instagramToken: "",
          instagramBusinessId: "",
          instagramAccountId: "",
          instagramPageId: "",
          instagramPageName: "",
          instagramAppId: "",
          instagramAppSecret: "",
          isInstagramExternalLogin: false,
          facebookToken: "",
          facebookAccountId: "",
          facebookAppId: "",
          facebookAppSecret: "",
          isFacebookExternalLogin: false,
          whatsappToken: "",
          whatsappBusinessId: "",
          whatsappBusinessPhoneNumber: "",
          phoneNumberId: "",
          TemplateName: "",
          whatsAppAppId: "",
          whatsAppAppSecret: "",
          isWhatsAppExternalLogin: false,
          messageDelivered: false,
          messageSeen: false,
          conversationStarted: false,
          active: false,
          development: false,
          messageReceived: false,
          messageMedia: false,
          messageReaction: false,
          messagePostback: false,
        },
      }),
    setFormData: (data) =>
      set((state) => ({
        formData: {
          name: data.name,
          AgentId: data.openAIAssistantId || "",
          facebookToken: data.accessToken,
          active: data.isActive || false,
          development: data.isDevelopment || false,
          facebookAccountId: data.facebookPageId || "",
          facebookAppId: data.facebookAppId || "",
          facebookAppSecret: data.appSecret || "",
          isFacebookExternalLogin: data.isExternalLogin || false,
          instagramAppId: data.instagramAppId || "",
          whatsAppAppId: data.whatsAppAppId || "",
          lastModified: data.lastModified || new Date().toISOString(),
        },
      })),
    setFacebookFormData: (data) =>
      set((state) => ({
        formData: {
          name: data.name || "",
          AgentId: data.openAIAssistantId || "",
          facebookToken: data.accessToken || "",
          active: data.isActive || false,
          development: data.isDevelopment || false,
          facebookAccountId: "", // This field doesn't exist in Messenger API response
          facebookPageId: data.facebookPageId || "",
          facebookPageName: data.facebookPageName || "",
          facebookAppId: data.facebookAppId || "",
          facebookAppSecret: data.appSecret || "",
          isFacebookExternalLogin: data.isExternalLogin || false,
          accessToken: data.accessToken || "",
          lastModified: data.lastModified || new Date().toISOString(),
        },
      })),
    setInstagramFormData: (data) =>
      set((state) => ({
        formData: {
          // Basic fields (support both camelCase and PascalCase from API)
          name: data?.name ?? data?.Name ?? "",
          AgentId: data?.openAIAssistantId ?? data?.OpenAIAssistantId ?? "",
          instagramToken: data?.accessToken ?? data?.AccessToken ?? "",
          active: data?.isActive ?? data?.IsActive ?? false,
          development: data?.isDevelopment ?? data?.IsDevelopment ?? false,

          // Instagram identifiers
          instagramBusinessId:
            data?.instagramBusinessId ?? data?.InstagramBusinessId ?? "",
          instagramAccountId:
            data?.instagramBusinessId ??
            data?.InstagramBusinessId ??
            data?.instagramAccount ??
            "",
          instagramPageId:
            data?.instagramPageId ??
            data?.InstagramPageId ??
            data?.facebookPageId ??
            data?.FacebookPageId ??
            "",
          instagramPageName:
            data?.instagramPageName ?? data?.InstagramPageName ?? "",

          // App credentials
          instagramAppId: data?.instagramAppId ?? data?.InstagramAppId ?? "",
          instagramAppSecret: data?.appSecret ?? data?.AppSecret ?? "",
          isInstagramExternalLogin:
            data?.isExternalLogin ?? data?.IsExternalLogin ?? false,

          lastModified:
            data?.lastModified ??
            data?.LastModified ??
            new Date().toISOString(),
        },
      })),

    setViberFormData: (data) =>
      set((state) => ({
        formData: {
          name: data.name,
          active: data.isActive || false,
          development: data.isDevelopment || false,
          isWebhookSubscribed: data.isWebhookSubscribed || false,
          AgentId: data.openAIAssistantId,
          viberToken: data.viberToken,
          lastModified: data.lastModified || new Date().toISOString(),
        },
      })),
    setWhatsAppFormData: (data) =>
      set((state) => ({
        formData: {
          ...state.formData, // Preserve existing form data
          // Map API response fields to form fields (API returns lowercase)
          ...(data.name !== undefined && { name: data.name }),
          ...(data.openAIAssistantId !== undefined && {
            AgentId: data.openAIAssistantId,
          }),
          ...(data.accessToken !== undefined && {
            whatsappToken: data.accessToken,
          }),
          ...(data.isActive !== undefined && { active: data.isActive }),
          ...(data.isDevelopment !== undefined && {
            development: data.isDevelopment,
          }),
          ...(data.whatsAppAppId !== undefined && {
            whatsAppAppId: data.whatsAppAppId,
          }),
          ...(data.appSecret !== undefined && {
            whatsAppAppSecret: data.appSecret,
          }),
          ...(data.isExternalLogin !== undefined && {
            isWhatsAppExternalLogin: data.isExternalLogin,
          }),
          ...(data.businessId !== undefined && {
            whatsappBusinessId: data.businessId,
          }),
          ...(data.businessPhoneNumber !== undefined && {
            whatsappBusinessPhoneNumber: data.businessPhoneNumber,
          }),
          ...(data.phoneNumberId !== undefined && {
            phoneNumberId: data.phoneNumberId,
          }),
          // Persist selected template name from API (supports both `templateName` and `TemplateName`)
          ...(() => {
            const incomingTemplateName =
              data?.templateName !== undefined
                ? data.templateName
                : data?.TemplateName !== undefined
                ? data.TemplateName
                : undefined;
            return incomingTemplateName !== undefined
              ? { TemplateName: incomingTemplateName }
              : {};
          })(),
          // Support both formats for business selection
          ...(data.whatsappBusinessId !== undefined && {
            whatsappBusinessId: data.whatsappBusinessId,
          }),
          ...(data.whatsappBusinessPhoneNumber !== undefined && {
            whatsappBusinessPhoneNumber: data.whatsappBusinessPhoneNumber,
          }),
        },
      })),

    handleSelectConfig: (config) => {
      set({
        // selectedConfigId: config.id,
        selectedConfig: config.id,
        isCreating: false,
      });
    },

    handleCreateNew: () => {
      get().resetForm();
      set({
        isCreating: true,
        // selectedConfigId: null,
        selectedConfig: null,
      });
    },

    handleInputChange: (id, value) => {
      set((state) => ({
        formData: {
          ...state.formData,
          [id]: value,
        },
      }));
    },

    // API-related actions
    setConfigLoading: (isLoading) => set({ isLoadingConfigs: isLoading }),

    setConfigError: (isError) => set({ isConfigError: isError }),

    setTransformedConfigurations: (platformId, apiData) => {
      if (!apiData) return;

      const transformedData = apiData.map((item) => ({
        id: item.id,
        name: item.name || "Unnamed",
        active: item.isActive || false,
        lastModified: item.lastModified || new Date().toISOString(),
      }));

      set((state) => ({
        configurations: {
          ...state.configurations,
          [platformId]: transformedData,
        },
      }));
    },
    platformIntegrations: {
      instagram: {
        isInitialized: false,
        accessToken: "",
        accountId: "",
        appId: "",
        appSecret: "",
      },
      facebook: {
        isInitialized: false,
        accessToken: "",
        accountId: "",
        appId: "",
        appSecret: "",
      },
      whatsapp: {
        isInitialized: false,
        accessToken: "",
        accountId: "",
        appId: "",
        appSecret: "",
        // Keep per-config credentials to avoid leaking tokens across configs
        tokensByConfig: {}, // { [configId]: { accessToken, userID } }
      },
      viber: {
        token: "",
        botName: "",
        webhookUrl: "",
      },
    },

    setFacebookInitialized: (isInitialized) =>
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          facebook: {
            ...state.platformIntegrations.facebook,
            isInitialized,
          },
        },
      })),

    setFacebookCredentials: (accessToken, accountId) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          facebook: {
            ...state.platformIntegrations.facebook,
            accessToken,
            accountId,
          },
        },
        formData: {
          ...state.formData,
          facebookToken: accessToken,
          facebookAccountId: accountId,
        },
      }));
    },

    setFacebookAppId: (appId) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          facebook: {
            ...state.platformIntegrations.facebook,
            appId,
          },
        },
        formData: {
          ...state.formData,
          facebookAppId: appId,
        },
      }));
    },
    setFacebookAppSecret: (appSecret) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          facebook: {
            ...state.platformIntegrations.facebook,
            appSecret,
          },
        },
        formData: {
          ...state.formData,
          facebookAppSecret: appSecret,
        },
      }));
    },
    setFacebookExternalLogin: (isExternalLogin) => {
      set((state) => ({
        formData: {
          ...state.formData,
          isFacebookExternalLogin: isExternalLogin,
          // Preserve values when toggling to prevent form refresh
          // We'll conditionally use these values based on isFacebookExternalLogin
        },
      }));
    },

    setInstagramInitialized: (isInitialized) =>
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          instagram: {
            ...state.platformIntegrations.instagram,
            isInitialized,
          },
        },
      })),

    setInstagramAppId: (appId) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          instagram: {
            ...state.platformIntegrations.instagram,
            appId,
          },
        },
        formData: {
          ...state.formData,
          instagramAppId: appId,
        },
      }));
    },

    setInstagramAppSecret: (appSecret) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          instagram: {
            ...state.platformIntegrations.instagram,
            appSecret,
          },
        },
        formData: {
          ...state.formData,
          instagramAppSecret: appSecret,
        },
      }));
    },

    setInstagramExternalLogin: (isExternalLogin) => {
      set((state) => ({
        formData: {
          ...state.formData,
          isInstagramExternalLogin: isExternalLogin,
          // Clear App ID and Secret when switching to internal login
          instagramAppId: isExternalLogin ? state.formData.instagramAppId : "",
          instagramAppSecret: isExternalLogin
            ? state.formData.instagramAppSecret
            : "",
        },
      }));
    },

    setInstagramCredentials: (accessToken, accountId) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          instagram: {
            ...state.platformIntegrations.instagram,
            accessToken,
            accountId,
          },
        },
        formData: {
          ...state.formData,
          instagramToken: accessToken,
          instagramBusinessId: accountId,
          instagramAccountId: accountId,
        },
      }));
    },

    setWhatsAppInitialized: (isInitialized) =>
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          whatsapp: {
            ...state.platformIntegrations.whatsapp,
            isInitialized,
          },
        },
      })),
    setWhatsAppAppId: (appId) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          whatsapp: {
            ...state.platformIntegrations.whatsapp,
            appId,
          },
        },
        formData: {
          ...state.formData,
          whatsAppAppId: appId,
        },
      }));
    },
    setWhatsAppAppSecret: (appSecret) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          whatsapp: {
            ...state.platformIntegrations.whatsapp,
            appSecret,
          },
        },
        formData: {
          ...state.formData,
          whatsAppAppSecret: appSecret,
        },
      }));
    },

    setWhatsAppExternalLogin: (isExternalLogin) => {
      set((state) => ({
        formData: {
          ...state.formData,
          isWhatsAppExternalLogin: isExternalLogin,
          // Clear App ID and Secret when switching to internal login
          whatsAppAppId: isExternalLogin ? state.formData.whatsAppAppId : "",
          whatsAppAppSecret: isExternalLogin
            ? state.formData.whatsAppAppSecret
            : "",
        },
      }));
    },

    setWhatsAppCredentials: (accessToken, userID) => {
      set((state) => {
        const selectedConfig = state.selectedConfig;
        const tokensByConfig = {
          ...(state.platformIntegrations.whatsapp.tokensByConfig || {}),
        };
        if (selectedConfig) {
          tokensByConfig[selectedConfig] = { accessToken, userID };
        }
        return {
          platformIntegrations: {
            ...state.platformIntegrations,
            whatsapp: {
              ...state.platformIntegrations.whatsapp,
              accessToken, // current active token reflects the active config
              userID,
              tokensByConfig,
            },
          },
          formData: {
            ...state.formData,
            whatsappToken: accessToken,
            // Don't set business ID here - it should be set when selecting a business
          },
        };
      });
    },

    clearWhatsAppCredentials: () => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          whatsapp: {
            ...state.platformIntegrations.whatsapp,
            accessToken: "",
            userID: "",
          },
        },
      }));
    },

    setWhatsAppBusinessPhone: (phoneNumber) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          whatsapp: {
            ...state.platformIntegrations.whatsapp,
            businessPhoneNumber: phoneNumber,
          },
        },
        formData: {
          ...state.formData,
          whatsappBusinessPhoneNumber: phoneNumber,
        },
      }));
    },

    setViberCredentials: (token, botName) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          viber: {
            ...state.platformIntegrations.viber,
            token,
            botName,
          },
        },
        formData: {
          ...state.formData,
          viberToken: token,
          viberBotName: botName,
        },
      }));
    },

    setViberWebhook: (webhookUrl) => {
      set((state) => ({
        platformIntegrations: {
          ...state.platformIntegrations,
          viber: {
            ...state.platformIntegrations.viber,
            webhookUrl,
          },
        },
        formData: {
          ...state.formData,
          webhookUrl,
        },
      }));
    },

    // Sets from when handleconfig is clicked
    fetchConfigurationById: async (id, platform) => {
      try {
        set({ isLoadingConfigs: true });
        const data = await socialPlatformsApi[platform].getById(id);
        get().setFormData(data);
        set({ isLoadingConfigs: false });
      } catch (error) {
        console.error("Error fetching configuration details:", error);
        set({ isConfigError: true, isLoadingConfigs: false });
      }
    },
  }))
);

export default useStore;
export { useStore };
